import React, { useCallback, useEffect, useId, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { trackEvent } from '../../analytics';
import { photoCountBucket } from '../../analytics-helpers';
import { MODE_ALL, resolveSelection } from '../../utils/daySelection';
import {
  COMPILE_RESOLUTIONS,
  DEFAULT_COMPILE_RESOLUTION,
  RESOLUTION_LABELS,
  compileCreditCharge,
} from '../../utils/credits';
import { ORIGIN_UPLOAD } from '../../utils/videoOrigin';
import { FPS_OPTIONS, DEFAULT_FPS } from './photoCompileOptions';

// "Create timelapse video" CTA — and, once a video exists, the rebuild
// affordance for it (#381). The server writes project.photoCompile
// { status, progress, fps, error? } from the callable so downstream states
// (queued / processing / failed) render off THAT field, not a local flag.
//
// Four shapes, decided in this order:
//   1. a compile is in flight / failed → progress or retry card (shown
//      whether or not a video already exists, so a REBUILD reports itself)
//   2. no source video → the first-compile CTA
//   3. a photo-compiled source video + photos added since it was built →
//      the rebuild CTA, which names what changed
//   4. an uploaded (or unprovenanced) source video → an explanatory notice,
//      not a compile offer. The server would refuse to overwrite it with
//      VIDEO_ALREADY_EXISTS, and there is no UI to delete the source video,
//      so silently rendering nothing left the user with photos and no way
//      to discover why nothing happened.

// Statuses in which the callable REFUSES a compile, so the CTA must not offer
// one. Kept in lock-step with _IN_FLIGHT_RENDER_STATUSES and
// _IN_FLIGHT_VIDEO_STATUSES in functions/timelapse/api/main.py — the server is
// the authority, these exist only so we explain the wait instead of firing a
// request that can only come back as a bare FAILED_PRECONDITION toast.
const IN_FLIGHT_RENDER_STATUSES = ['queued', 'processing'];
const IN_FLIGHT_SOURCE_VIDEO_STATUSES = ['queued', 'uploading', 'analyzing'];

// Mirrors MIN_PHOTOS_FOR_COMPILE server-side. UX-only: the callable re-checks.
const MIN_PHOTOS = 2;
// Human labels for the worker-written photoCompile.phase (photo_compile.py).
// Unknown/absent phases render as the bare percent, so an old in-flight doc
// (or a worker mid-deploy) degrades to the previous UI rather than "undefined".
const PHASE_LABELS = {
  downloading: 'Downloading photos',
  encoding: 'Encoding video',
  finalizing: 'Finishing up',
};
// How long the day selection must settle before we ask the server to cost it.
// Clicking through a week of days should cost one call, not seven.
const PREVIEW_DEBOUNCE_MS = 350;

// Why a compile is refused over an existing video, in the user's words.
//
// The server's rule — and its client mirror, canRecompile() in
// utils/videoOrigin.js — refuses BOTH an uploaded video and one whose
// provenance was never stamped, and those are different things to tell someone.
// "You uploaded this" is a claim about what the user did; `unknown` is us
// admitting we can't tell. Projects compiled before video.origin existed whose
// photoCompile.outputPath no longer matches video.storagePath resolve to
// `unknown` despite being real photo compiles, so asserting "uploaded" there
// would be flatly false. Anything that is not provably an upload gets the
// hedge.
function blockedReason(origin) {
  return origin === ORIGIN_UPLOAD
    ? 'This project’s video was uploaded directly, and compiling photos would replace it.'
    : 'We can’t confirm this project’s video was built from these photos, so compiling them would risk replacing footage you may not be able to get back.';
}

function formatDurationMSS(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Layout shapes. Same component, same state, same callable — only the frame
// around the controls differs:
//   'full'   — the standalone card this has always been. Still what renders
//              for an in-flight/failed/blocked compile, which are cards in
//              their own right rather than a button on someone else's.
//   'action' — the compact form that lives in CompiledVideoCard: its actions
//              column over an existing video, its dashed band when there is
//              none. One priced button with the frame rate / resolution /
//              day-scope controls laid out inline beside or beneath it, always
//              visible. They spent a release behind a "Settings" disclosure and
//              that was wrong: these controls are what the price on the button
//              is quoting, so folding them away hides the terms of the purchase
//              and makes 4K feel like it isn't on offer.
export const VARIANT_FULL = 'full';
export const VARIANT_ACTION = 'action';

// How loudly the action variant's button paints. This is about PLACEMENT, not
// about what the button does: 'primary' is the lone CTA in a frame of its own,
// 'secondary' is one action stacked under a louder one the host owns. Only the
// host knows which, so the host says.
export const EMPHASIS_PRIMARY = 'primary';
export const EMPHASIS_SECONDARY = 'secondary';

const PhotoCompileCta = ({
  projectId,
  photoCount,
  hasSourceVideo,
  photoCompile,
  uploadInProgress,
  variant = VARIANT_FULL,
  // Defaults to the loud treatment: a CTA nobody has claimed rank over is the
  // primary. Ignored outside the action variant — the full card frames its own
  // button and has no column to share.
  emphasis = EMPHASIS_PRIMARY,
  // #381: recompile inputs. `recompilable` says the existing video is a
  // photo-compile output the server will let us rebuild; `sinceCompile` is
  // the {newPhotos, newDays} diff vs the last compile (null = unknown).
  recompilable = false,
  // The classification behind `recompilable` ('upload' | 'unknown' |
  // 'photo-compile' | null), from resolveVideoOrigin(). Only used to word the
  // refusal accurately; defaults to null so an un-passed origin gets the
  // hedged copy rather than a false "you uploaded this".
  videoOrigin = null,
  sinceCompile = null,
  // #381: the two other things the callable checks before it will start a
  // compile. `editedVideoStatus` is project.editedVideo.status (a charged
  // render still reading the source); `sourceVideoStatus` is
  // project.video.status (the previous compile's mp4 still landing).
  editedVideoStatus = null,
  sourceVideoStatus = null,
  // #385: day selection. `daySelection` is the useDaySelection() hook's return
  // value, owned by FootageTab so PhotoDayGrid renders the same choice this
  // CTA costs and sends. `coverage` is the meta/coverage rollup, used for the
  // instant local estimate the server preview then confirms.
  daySelection = null,
  coverage = null,
  // Billing. `paidCredits` is project.compileBilling.paidCredits — the running
  // total of compile credits already charged on this project, which the server
  // subtracts from the price of the next compile. `onInsufficientCredits`
  // hands a shortfall back up to ProjectView so it opens the same
  // InsufficientCreditsDialog the render path uses.
  paidCredits = 0,
  onInsufficientCredits = null,
  // #346: the account is over its storage allowance, so the server refused to
  // create another video. Handed up to FootageTab, which opens the same
  // storage upsell the upload preflight uses — the remedy is identical (free
  // up space or move to a bigger plan), so the prompt should be too.
  onStorageFrozen = null,
}) => {
  const [busy, setBusy] = useState(false);
  const [invocationError, setInvocationError] = useState(null);
  // Seed from the server-persisted choice so a refresh/remount (notably the
  // failed-compile retry path, which reuses this state) keeps the fps the
  // user picked instead of snapping back to DEFAULT_FPS.
  const [fps, setFps] = useState(() => {
    const savedFps = photoCompile?.fps;
    return FPS_OPTIONS.includes(savedFps) ? savedFps : DEFAULT_FPS;
  });
  const fpsSelectId = useId();
  // Same seed-from-the-server rule as fps: a retry after a failed 4K compile
  // must not silently drop back to 1080p and rebuild something cheaper than
  // what the user asked for.
  const [resolution, setResolution] = useState(() => {
    const saved = photoCompile?.outputResolution;
    return COMPILE_RESOLUTIONS.includes(saved) ? saved : DEFAULT_COMPILE_RESOLUTION;
  });
  const resolutionSelectId = useId();

  // --- Day selection (#385) -------------------------------------------------
  // The scope is expressed as a wire selection; the counts come from two
  // places that should always agree — an instant local resolution against the
  // coverage rollup we already hold, and the server preview that confirms it
  // against live coverage. Local renders immediately so a checkbox never feels
  // laggy; the server's answer replaces it when it lands.
  const selection = daySelection?.selection || null;
  const choosing = !!daySelection?.choosing;
  const scoped = !!selection && selection.mode !== MODE_ALL;
  const selectionKey = selection ? JSON.stringify(selection) : null;

  const localResolved = scoped
    ? resolveSelection(selection, coverage?.days)
    : null;
  const localCount = localResolved ? localResolved.photoCount : photoCount;

  const [preview, setPreview] = useState(null);

  useEffect(() => {
    // "All days" needs no server call — photoCount already IS the answer.
    // Neither does a selection too small to compile: the button is disabled
    // on the local count, so a round trip could only confirm the obvious.
    if (!scoped || localCount < MIN_PHOTOS) {
      setPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      const previewCompile = httpsCallable(getFunctions(), 'preview_photo_compile');
      previewCompile({ projectId, selection: JSON.parse(selectionKey), fps })
        .then((res) => {
          // Tagged with the selection it describes so a slow response for an
          // earlier choice can never overwrite the current one.
          if (!cancelled) setPreview({ key: selectionKey, fps, data: res.data });
        })
        .catch(() => {
          // Preview is advisory — the compile callable re-validates. Falling
          // back to the local estimate beats blocking on a failed estimate.
          if (!cancelled) setPreview(null);
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectId, selectionKey, fps, scoped, localCount]);

  const confirmed = preview && preview.key === selectionKey && preview.fps === fps
    ? preview.data
    : null;
  // Server answer wins once it lands; local covers the gap before it does.
  const selectedPhotoCount = confirmed ? confirmed.photoCount : localCount;
  const selectedDayCount = confirmed
    ? confirmed.dayCount
    : (localResolved ? localResolved.dayCount : (daySelection?.allDayKeys?.length ?? 0));
  const missingDays = confirmed ? confirmed.missingDays : (localResolved?.missingDays || []);
  const totalDayCount = daySelection?.allDayKeys?.length ?? 0;
  // Only blocks when the SELECTION is the problem; every other precondition is
  // reported through unavailableReason below.
  const selectionTooSmall = scoped && selectedPhotoCount < MIN_PHOTOS;

  // What this compile will actually charge. Mirrors the server's
  // max(0, cost - paidCredits), so a rebuild of something already paid for
  // quotes 0 rather than re-quoting the full price. null = not costable yet
  // (no photos resolved), in which case the price row is hidden rather than
  // guessed at.
  const creditCharge = compileCreditCharge({
    photoCount: selectedPhotoCount,
    fps,
    resolution,
    paidCredits,
  });
  const priceLabel = creditCharge === null
    ? null
    : creditCharge === 0
      // "Included" not "Free": the user DID pay for this compile, on an
      // earlier run. Calling it free would imply compiling never costs.
      ? 'Included — already paid for'
      : `${creditCharge} ${creditCharge === 1 ? 'credit' : 'credits'}`;

  const status = photoCompile?.status || null;
  // A rebuild of an existing photo-compiled video, as opposed to the first
  // compile. Only affects copy + analytics — the callable is identical.
  const isRecompile = hasSourceVideo && recompilable;

  // The price ON the button, for the compact variant: in the card there is no
  // separate Cost row to read it off, and a button that spends credits without
  // saying how many is the one thing a credit product must never ship.
  const priceSuffix = creditCharge === null
    ? ''
    : creditCharge === 0
      ? ' · included'
      : ` · ${creditCharge} ${creditCharge === 1 ? 'credit' : 'credits'}`;
  const actionLabel = isRecompile
    ? `Recompile${priceSuffix}`
    : `Compile footage${priceSuffix}`;

  // Why the compile is unavailable right now, or null when it can be started.
  //   * uploadInProgress: the photo count is still a partial snapshot, so
  //     compiling now would render a near-empty video.
  //   * the other two mirror the server. canRecompile() only encodes the
  //     PROVENANCE half of the callable's rule; it additionally refuses with
  //     RENDER_IN_FLIGHT while a charged render is still reading the source
  //     (replacing it would render new footage on the old charge) and with
  //     COMPILE_IN_FLIGHT while the previous compile's mp4 is still
  //     uploading/analyzing. Photos landing mid-render is the #381 scenario, so
  //     that window is not rare — without this the user clicks Rebuild and gets
  //     an unexplained FAILED_PRECONDITION.
  // Ordered most-immediate-first; only one is shown.
  const unavailableReason = uploadInProgress
    ? 'Available when the upload finishes.'
    : IN_FLIGHT_RENDER_STATUSES.includes(editedVideoStatus)
      ? 'Available when the current render finishes.'
      : IN_FLIGHT_SOURCE_VIDEO_STATUSES.includes(sourceVideoStatus)
        ? 'Available when the video finishes processing.'
        : null;
  // Disable (don't hide) while blocked: every reason above clears on its own,
  // which re-enables the CTA without the user doing anything.
  const compileDisabled = busy || !!unavailableReason || selectionTooSmall;

  const startCompile = useCallback(async () => {
    if (busy) return;
    setInvocationError(null);
    setBusy(true);
    trackEvent('photo_compile_requested', {
      project_id: projectId,
      photo_count: selectedPhotoCount,
      count_bucket: photoCountBucket(selectedPhotoCount),
      fps,
      is_retry: status === 'failed',
      is_recompile: isRecompile,
      new_photos: sinceCompile?.newPhotos ?? 0,
      // #385: is this a scoped compile, and how much of the project did it
      // cover? Answers "do people actually use day selection?".
      day_scoped: scoped,
      day_count: selectedDayCount,
      output_resolution: resolution,
      credit_charge: creditCharge ?? 0,
    });
    try {
      const functions = getFunctions();
      const compilePhotos = httpsCallable(functions, 'compile_photos');
      // Omitted entirely when unscoped, so the server persists the compact
      // {mode:'all'} and a later rebuild picks up days added since.
      await compilePhotos({
        projectId,
        fps,
        outputResolution: resolution,
        ...(scoped ? { selection: JSON.parse(selectionKey) } : {}),
      });
    } catch (err) {
      const reason = err?.details?.reason;
      // Read the structured reason, never err.message: on a transport failure
      // the Firebase SDK sets .message to the bare error code, so a
      // `message || fallback` test never reaches its fallback.
      if (reason === 'INSUFFICIENT_CREDITS') {
        const required = Number(err?.details?.requiredCredits);
        const shortfall = Number.isFinite(required) ? required : creditCharge;
        if (onInsufficientCredits) {
          // Same destination as the render path's shortfall: the top-level
          // InsufficientCreditsDialog, which offers a way to buy more.
          onInsufficientCredits(shortfall);
        } else {
          setInvocationError(
            `Not enough credits — this compile needs ${shortfall ?? 'more'} credits.`,
          );
        }
      } else if (reason === 'ACCOUNT_STORAGE_FROZEN') {
        // Over the pooled allowance after a cancel or downgrade (#346).
        // Nothing has been deleted; the account just can't create new videos
        // until it fits again, which is exactly the storage upsell's case.
        if (onStorageFrozen) {
          onStorageFrozen(err?.details || {});
        } else {
          setInvocationError(
            'Your account is over its storage limit. Free up space or upgrade '
            + 'your plan to create new videos.',
          );
        }
      } else if (reason === 'INVALID_OUTPUT_RESOLUTION') {
        setInvocationError('That output resolution is not available. Pick 1080p or 4K.');
      } else if (reason === 'SELECTION_NOT_ENOUGH_PHOTOS') {
        setInvocationError(
          `The selected days hold ${err.details.photoCount ?? 0} photos; at least ${MIN_PHOTOS} are needed.`,
        );
      } else if (reason === 'COVERAGE_UNAVAILABLE') {
        setInvocationError('Day information is still being prepared. Try again in a moment.');
      } else if (err && err.code === 'functions/invalid-argument') {
        setInvocationError('That day selection is not valid. Try selecting the days again.');
      } else if (reason === 'VIDEO_ALREADY_EXISTS') {
        // The server tells us which case it refused on (details.origin is
        // 'upload' or 'unknown'), so the message states the true one instead
        // of guessing.
        setInvocationError(`${blockedReason(err?.details?.origin)} Create a new project for these photos.`);
      } else if (err && err.code === 'functions/failed-precondition') {
        setInvocationError('Photo compilation is temporarily unavailable.');
      } else {
        setInvocationError('Could not start compilation. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [
    busy, projectId, selectedPhotoCount, status, fps, isRecompile, sinceCompile,
    scoped, selectionKey, selectedDayCount, resolution, creditCharge,
    onInsufficientCredits, onStorageFrozen,
  ]);

  if (!photoCount || photoCount <= 0) return null;

  // In-flight/failed states render even when a video already exists — that is
  // exactly the recompile case, and hiding it would make a rebuild look like
  // nothing happened. (Pre-#381 the hasSourceVideo bail came first, so a
  // compile could never be in flight here at all.) They stay hidden over a
  // video we could never be rebuilding, so a wedged status from some earlier
  // state can't haunt an uploaded video's tab.
  const compileStateIsOurs = !hasSourceVideo || isRecompile;

  // The day-scope row. Declared as a function so the retry card below can show
  // it too — without it, a failed scoped compile leaves checkboxes on the grid
  // with nothing explaining what is ticked or how to change it.
  function renderDaysControl() {
    if (!daySelection || totalDayCount <= 1) return null;
    return (
      <div className="pi-day-scope" data-testid="photo-day-scope" data-choosing={String(choosing)}>
        <div className="pi-day-scope-summary">
          {choosing
            ? `${selectedDayCount.toLocaleString()} of ${totalDayCount.toLocaleString()} days · ${selectedPhotoCount.toLocaleString()} photo${selectedPhotoCount === 1 ? '' : 's'}`
            : `All ${totalDayCount.toLocaleString()} days`}
        </div>
        <div className="pi-day-scope-actions">
          {choosing ? (
            <>
              <button type="button" className="btn ghost pi-btn-sm" onClick={daySelection.selectAll}>
                Select all
              </button>
              <button type="button" className="btn ghost pi-btn-sm" onClick={daySelection.clearAll}>
                Clear
              </button>
              <button
                type="button"
                className="btn ghost pi-btn-sm"
                data-testid="photo-day-scope-cancel"
                onClick={daySelection.stopChoosing}
              >
                Use all days
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn ghost pi-btn-sm"
              data-testid="photo-day-scope-choose"
              onClick={daySelection.startChoosing}
            >
              Choose days
            </button>
          )}
        </div>
        {choosing && (
          <div className="pi-day-scope-hint">
            Tick the days to include below. Shift-click to select a range.
            {missingDays.length > 0
              && ` ${missingDays.length.toLocaleString()} selected day${missingDays.length === 1 ? ' no longer holds' : 's no longer hold'} photos.`}
          </div>
        )}
      </div>
    );
  }

  // The two output pickers — frame rate and resolution. Deliberately ONE
  // definition shared by both variants rather than a copy per layout: the
  // compact form re-flows them, it does not simplify them, and a duplicated
  // picker is how one copy quietly loses its 4K option and starts selling a
  // different product than the other.
  function renderSpecSelects() {
    return (
      <>
        <div className="pi-fps-field">
          <label className="pi-fps-label" htmlFor={fpsSelectId}>
            Frame rate
          </label>
          <div className="pi-fps-select-wrap">
            <select
              id={fpsSelectId}
              className="pi-fps-select"
              data-testid="photo-fps-control"
              value={fps}
              onChange={(e) => setFps(Number(e.target.value))}
              disabled={compileDisabled}
            >
              {FPS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt} fps
                </option>
              ))}
            </select>
            <span className="pi-fps-chevron" aria-hidden="true">
              ▾
            </span>
          </div>
        </div>

        <div className="pi-fps-field">
          <label className="pi-fps-label" htmlFor={resolutionSelectId}>
            Resolution
          </label>
          <div className="pi-fps-select-wrap">
            <select
              id={resolutionSelectId}
              className="pi-fps-select"
              data-testid="photo-resolution-control"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              disabled={compileDisabled}
            >
              {COMPILE_RESOLUTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {RESOLUTION_LABELS[opt] || opt}
                </option>
              ))}
            </select>
            <span className="pi-fps-chevron" aria-hidden="true">
              ▾
            </span>
          </div>
        </div>
      </>
    );
  }

  // The full card's spec row: the same two pickers, plus what the pair costs.
  // The Cost row is full-variant ONLY. In the compact form the price is already
  // on the button (`priceSuffix`), and a second copy of it a few pixels below
  // reads as a second charge rather than the same one restated.
  function renderSpecControls() {
    return (
      <>
        {renderSpecSelects()}

        {priceLabel && (
          <div className="pi-compile-price" data-testid="photo-compile-price">
            <span className="pi-fps-label">Cost</span>
            <span className="pi-compile-price-value">{priceLabel}</span>
          </div>
        )}
      </>
    );
  }

  if (compileStateIsOurs && (status === 'queued' || status === 'processing')) {
    const pct = Math.min(100, Math.max(0, photoCompile?.progress || 0));
    // The compile's OWN photo count — a scoped compile encodes a subset, so
    // the project total would overstate what is actually being rendered.
    const compilingCount = typeof photoCompile?.photoCount === 'number'
      ? photoCompile.photoCount
      : photoCount;
    return (
      <div className="pi-compile-card" data-testid="photo-compile-card" data-state={status}>
        <div className="pi-compile-head">
          <h4>
            {isRecompile ? 'Rebuilding' : 'Compiling'} timelapse from {compilingCount.toLocaleString()} photos…
          </h4>
        </div>
        <div className="pi-progress-track">
          <div className="pi-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="pi-compile-sub">
          {status === 'queued'
            ? 'Queued'
            : `${PHASE_LABELS[photoCompile?.phase] ? `${PHASE_LABELS[photoCompile.phase]} — ` : ''}${Math.floor(pct)}%`}
        </div>
      </div>
    );
  }

  if (compileStateIsOurs && status === 'failed') {
    return (
      <div className="pi-compile-card pi-compile-card--error" data-testid="photo-compile-card" data-state="failed">
        <div className="pi-compile-head">
          <h4>Compilation failed</h4>
        </div>
        <div className="pi-compile-sub">
          {photoCompile?.error || 'Something went wrong compiling your photos into a video.'}
        </div>
        {/* Retry reuses the current scope, so the user must be able to see and
            change it here rather than only on a fresh CTA. */}
        {renderDaysControl()}
        <button type="button" className="btn btn-teal capsule" onClick={startCompile} disabled={compileDisabled}>
          Retry
        </button>
        {unavailableReason && <div className="pi-compile-sub">{unavailableReason}</div>}
      </div>
    );
  }

  // A source video the server will not let us replace (uploaded footage, or
  // provenance we can't prove) — offering a compile would just earn a
  // VIDEO_ALREADY_EXISTS. Render an explanatory notice instead of an offer:
  // the early photoCount bail above already guarantees there are photos
  // here, so silence would leave the user staring at a project with photos
  // and no compile CTA and no clue why.
  if (hasSourceVideo && !recompilable) {
    return (
      <div className="pi-compile-card" data-testid="photo-compile-blocked">
        <div className="pi-compile-head">
          <h4>These photos can’t be compiled here</h4>
        </div>
        <div className="pi-compile-sub">
          {blockedReason(videoOrigin)} To build a timelapse from {photoCount === 1 ? 'this photo' : `these ${photoCount.toLocaleString()} photos`}, create a new project and upload {photoCount === 1 ? 'it' : 'them'} there.
        </div>
      </div>
    );
  }
  // Nothing new has landed since the video was built, so there is nothing to
  // rebuild from. Stay out of the way until the user adds photos.
  //
  // `outsidePhotos` keeps the CTA alive when there is footage on days the last
  // compile did NOT cover (#385). Rebuilding as-scoped would not include it,
  // but hiding the CTA would strand the user with no way to widen the
  // selection — the one state where "nothing to rebuild" is actively wrong.
  const newPhotos = sinceCompile?.newPhotos ?? 0;
  const outsidePhotos = sinceCompile?.photosOutsideSelection ?? 0;
  if (hasSourceVideo && newPhotos <= 0 && outsidePhotos <= 0) return null;

  // The compact form. Everything above still applies — same preconditions,
  // same price, same callable — this only trades the explanatory card for a
  // button, because the card it now sits in has already told the story (the
  // amber "N photos added since this was compiled" chip is the same fact
  // `recompileSummary` spells out below).
  if (variant === VARIANT_ACTION) {
    return (
      <div
        className="pi-compile-action"
        data-testid="photo-compile-cta"
        data-variant="action"
        data-mode={isRecompile ? 'recompile' : 'compile'}
      >
        {/* How loudly this button paints is the HOST's call, not ours: the same
            compact form is the lone CTA in the empty-state band (teal, it is the
            primary) and one of three stacked actions in the compiled card's
            column (quiet, the teal there belongs to "Fine-tune in Editor").
            Only the host knows which, so it passes `emphasis` and we default to
            primary. Deliberately NOT keyed off `isRecompile` — a rebuild is not
            the same fact as "something louder sits above me", and conflating
            them silently mispaints this button the moment either changes.

            A data attribute rather than one of the card's own classes: that
            vocabulary belongs to ProjectTabs.css, which this file neither
            imports nor should know about. That stylesheet opts this button in
            by attribute, exactly as it already does for the button's width. A
            test in this file's suite fails if the class name ever reappears
            here, comment included. */}
        <button
          type="button"
          className={emphasis === EMPHASIS_SECONDARY
            ? 'btn capsule pi-compile-cta-btn'
            : 'btn btn-teal capsule pi-compile-cta-btn'}
          data-emphasis={emphasis === EMPHASIS_SECONDARY ? EMPHASIS_SECONDARY : undefined}
          onClick={startCompile}
          disabled={compileDisabled}
        >
          {busy ? 'Starting…' : actionLabel}
        </button>
        {/* Inline and always visible — see the variant note at the top of this
            file. No Cost row here: the button already carries the price.
            renderDaysControl() no-ops on a single-day project, so this is a
            bare pair of selects unless there is genuinely a scope to choose. */}
        <div className="pi-compile-settings" data-testid="photo-compile-settings">
          <div className="pi-compile-inline-settings">{renderSpecSelects()}</div>
          {renderDaysControl()}
          {/* Only the rebuild needs a caveat, and only about the one thing
              users fear: that recompiling eats the renders they already paid
              for. The generic "higher frame rates play smoother" hint is
              omitted on purpose — State C's band already carries a note, and
              in a 200px column it is filler next to a priced button. */}
          {isRecompile && (
            <p className="pi-compile-settings-note">
              The rebuilt video replaces this one. Videos you’ve already rendered are kept.
            </p>
          )}
        </div>
        {/* The same two refusals the full card words in its subtitle. Without
            them a disabled button in a 200px column has no explanation at
            all, which is how "the button does nothing" bug reports start. */}
        {unavailableReason && <div className="pi-compile-sub">{unavailableReason}</div>}
        {!unavailableReason && selectionTooSmall && (
          <div className="pi-compile-sub">
            Select at least {MIN_PHOTOS} photos&apos; worth of days to compile.
          </div>
        )}
        {invocationError && <div className="pi-compile-error">{invocationError}</div>}
      </div>
    );
  }

  const seconds = (confirmed ? confirmed.estimatedSeconds : selectedPhotoCount / fps) || 0;
  const newDays = sinceCompile?.newDays ?? 0;
  const scopePreview = `${scoped ? '' : 'all '}${selectedPhotoCount.toLocaleString()} photos → about ${formatDurationMSS(seconds)} at ${fps} fps.`;
  // Two different situations, and conflating them was the bug this replaced:
  //   newPhotos      — arrived since the build, INSIDE the scope. A rebuild
  //                    picks them up, so "rebuild to include them" is true.
  //   outsidePhotos  — sitting on days the scope never covered. A rebuild does
  //                    NOT pick them up; the user has to widen the selection
  //                    first. It is also not an arrival figure, so it must not
  //                    be called "new" or said to have "landed".
  const recompileSummary = newPhotos > 0
    ? `${newPhotos.toLocaleString()} new photo${newPhotos === 1 ? '' : 's'}`
      + (newDays > 0 ? ` across ${newDays.toLocaleString()} new day${newDays === 1 ? '' : 's'}` : '')
      + ` since your video was built. Rebuild to include ${newPhotos === 1 ? 'it' : 'them'} — ${scopePreview}`
    : `${outsidePhotos.toLocaleString()} photo${outsidePhotos === 1 ? ' sits' : 's sit'} on days your video does not cover — add those days above to include ${outsidePhotos === 1 ? 'it' : 'them'}. As scoped now: ${scopePreview}`;

  const daysControl = renderDaysControl();

  return (
    <div
      className="pi-compile-cta"
      data-testid="photo-compile-cta"
      data-mode={isRecompile ? 'recompile' : 'compile'}
    >
      <div className="pi-compile-cta-header">
        <div className="pi-compile-cta-title">
          {isRecompile ? 'Rebuild timelapse video' : 'Create timelapse video'}
        </div>
        <div className="pi-compile-cta-sub" data-testid="photo-compile-summary">
          {unavailableReason
            || (selectionTooSmall
              ? `Select at least ${MIN_PHOTOS} photos' worth of days to compile.`
              : isRecompile
                ? recompileSummary
                : `${selectedPhotoCount.toLocaleString()} photos → about ${formatDurationMSS(seconds)} at ${fps} fps.`)}
        </div>
      </div>

      {daysControl}

      <div className="pi-compile-cta-controls">
        {renderSpecControls()}

        <button
          type="button"
          className="btn btn-teal capsule pi-compile-cta-btn"
          onClick={startCompile}
          disabled={compileDisabled}
        >
          {busy ? 'Starting…' : isRecompile ? 'Rebuild timelapse video' : 'Create timelapse video'}
        </button>
      </div>

      <p className="pi-fps-hint">
        {isRecompile
          ? 'The rebuilt video replaces the current one. Videos you have already rendered are kept.'
          : 'Higher frame rates play smoother but make a shorter video.'}
        {' '}
        {creditCharge === 0
          ? 'You have already paid for this build — rebuilding at the same settings costs nothing.'
          : 'You only pay the difference if you rebuild at a higher setting later.'}
      </p>

      {invocationError && <div className="pi-compile-error">{invocationError}</div>}
    </div>
  );
};

export default PhotoCompileCta;
